/**
 * One car's availability, layered on top of the booking date rules.
 *
 * `dates` is a createDateRules() object (lead time, trip length, horizon,
 * whole hours - see dateRange.js). `busy` is the car's booked windows from
 * GET /admin/booking/car/:carId/busy. A trip [start, end) is free when it
 * overlaps no booked window - the exact predicate the server uses
 * (BookingUtils.checkAvailability: busy.start < end && busy.end > start), so
 * back-to-back trips are allowed.
 *
 * Every function is pure and works on the IST clock through dateRange.js.
 * With an empty `busy` list everything here behaves exactly like the plain
 * rules, so the picker can always go through this layer.
 *
 * Hour status values used by the calendar and the time sliders:
 *   "ok"    can be chosen
 *   "rule"  outside lead time / trip length / horizon
 *   "busy"  the car is booked then, or there isn't a minimum-length trip's
 *           room before its next booking
 */

import { floorToHour, startOfDay, withHour } from "./dateRange";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const parse = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Accepts { start, end } or { startDate, endDate } with Dates, ISO strings or epoch ms. */
export const normalizeBusy = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item) => ({
      start: parse(item?.start ?? item?.startDate),
      end: parse(item?.end ?? item?.endDate),
      bookingId: item?.bookingId,
      stage: item?.stage,
    }))
    .filter((item) => item.start && item.end && item.end.getTime() > item.start.getTime())
    .sort((a, b) => a.start.getTime() - b.start.getTime());

export const createAvailability = (dates, busyInput = []) => {
  const busy = normalizeBusy(busyInput);
  const minTripMs = dates.rules.minTripHours * HOUR_MS;

  /** The booked window covering moment `t`, if any. */
  const busyAt = (t) => {
    const ms = t.getTime();
    return busy.find((b) => b.start.getTime() <= ms && ms < b.end.getTime()) || null;
  };

  /** Start of the first booking that begins at or after `t`. */
  const nextBusyStart = (t) => {
    const ms = t.getTime();
    const next = busy.find((b) => b.start.getTime() >= ms);
    return next ? next.start : null;
  };

  /** Latest legal drop-off for a pickup: the trip-length cap or the next booking, whichever is sooner. */
  const endLimit = (start) => {
    const max = dates.maxEndFor(start);
    const next = nextBusyStart(start);
    if (!next) return max;
    const cap = floorToHour(next);
    return cap.getTime() < max.getTime() ? cap : max;
  };

  const isFreeStart = (start) => !busyAt(start) && endLimit(start).getTime() - start.getTime() >= minTripMs;

  const isRangeFree = (range) => {
    const s = parse(range?.start);
    const e = parse(range?.end);
    if (!s || !e) return false;
    return !busy.some((b) => b.start.getTime() < e.getTime() && b.end.getTime() > s.getTime());
  };

  /* ---------------- per-hour status ---------------- */

  const startHourStatus = (day, now = new Date()) => {
    const selectable = dates.isDaySelectable(day, now);
    const { min, max } = dates.startHourRange(day, now);
    return Array.from({ length: 24 }, (_, hour) => {
      if (!selectable || hour < min || hour > max) return "rule";
      return isFreeStart(withHour(day, hour)) ? "ok" : "busy";
    });
  };

  const endHourStatus = (day, start) => {
    const hostable = dates.canHostEnd(day, start);
    const { min, max } = dates.endHourRange(day, start);
    const limit = endLimit(start).getTime();
    return Array.from({ length: 24 }, (_, hour) => {
      if (!hostable || hour < min || hour > max) return "rule";
      return withHour(day, hour).getTime() > limit ? "busy" : "ok";
    });
  };

  /** Booked windows touching a calendar day (for tooltips and the partial-day marker). */
  const windowsOnDay = (day) => {
    const from = startOfDay(day).getTime();
    const to = from + DAY_MS;
    return busy.filter((b) => b.start.getTime() < to && b.end.getTime() > from);
  };

  /** "full" when the car is booked the whole day, "partial" when part of it, null when free. */
  const dayBooking = (day) => {
    const windows = windowsOnDay(day);
    if (!windows.length) return null;
    const from = startOfDay(day).getTime();
    const to = from + DAY_MS;
    let cursor = from;
    for (const w of windows) {
      if (w.start.getTime() > cursor) return "partial";
      cursor = Math.max(cursor, w.end.getTime());
      if (cursor >= to) return "full";
    }
    return cursor >= to ? "full" : "partial";
  };

  /* ---------------- picking ---------------- */

  const nearestOk = (statuses, preferred) => {
    let best = null;
    statuses.forEach((status, hour) => {
      if (status !== "ok") return;
      if (best === null || Math.abs(hour - preferred) < Math.abs(best - preferred)) best = hour;
    });
    return best;
  };

  /** Pickup on `day` at the free hour closest to `preferredHour`, or null. */
  const pickStart = (day, preferredHour, now = new Date()) => {
    const hour = nearestOk(startHourStatus(day, now), preferredHour);
    return hour === null ? null : withHour(day, hour);
  };

  /** Drop-off on `day` at the free hour closest to `preferredHour`, or null. */
  const pickEnd = (day, start, preferredHour) => {
    const hour = nearestOk(endHourStatus(day, start), preferredHour);
    return hour === null ? null : withHour(day, hour);
  };

  /** Clamp a drop-off into [minEnd, endLimit] for this pickup. */
  const fitEnd = (start, end) => {
    const minEnd = dates.minEndFor(start);
    const limit = endLimit(start);
    let value = parse(end) ? floorToHour(parse(end)) : minEnd;
    if (value.getTime() < minEnd.getTime()) value = minEnd;
    if (value.getTime() > limit.getTime()) value = limit;
    return value;
  };

  /** A range with the drop-off moved so it runs `hours` long, clamped to what's free. */
  const withLength = (start, hours) => ({ start, end: fitEnd(start, new Date(start.getTime() + hours * HOUR_MS)) });

  /** Longest trip (in whole hours) that can start at `start`. */
  const maxTripHoursFrom = (start) => Math.floor((endLimit(start).getTime() - start.getTime()) / HOUR_MS);

  /** Earliest free pickup at or after `from` (hour steps up to the horizon), or null. */
  const firstFreeStartFrom = (from, now = new Date()) => {
    const latest = dates.latestStart(now).getTime();
    for (let t = dates.clampStart(from, now).getTime(); t <= latest; t += HOUR_MS) {
      const start = new Date(t);
      if (isFreeStart(start)) return start;
    }
    return null;
  };

  /** Latest free pickup at or before `from`, or null. */
  const lastFreeStartBefore = (from, now = new Date()) => {
    const earliest = dates.earliestStart(now).getTime();
    for (let t = dates.clampStart(from, now).getTime(); t >= earliest; t -= HOUR_MS) {
      const start = new Date(t);
      if (isFreeStart(start)) return start;
    }
    return null;
  };

  /**
   * Force any range into one this car can take. If the pickup falls on a
   * booking, it moves to the next free pickup (or the previous one when
   * nothing is free later), keeping the trip length where it can.
   * `moved` says whether anything changed; `blocked` that nothing is free at all.
   */
  const fitRange = (range, now = new Date()) => {
    const base = dates.clampRange(range, now);
    const length = base.end.getTime() - base.start.getTime();
    if (isFreeStart(base.start)) {
      const end = fitEnd(base.start, base.end);
      return { start: base.start, end, moved: end.getTime() !== base.end.getTime(), blocked: false };
    }
    const start = firstFreeStartFrom(base.start, now) || lastFreeStartBefore(base.start, now);
    if (!start) return { ...base, moved: false, blocked: true };
    return { start, end: fitEnd(start, new Date(start.getTime() + length)), moved: true, blocked: false };
  };

  const setStartHour = (hour, range, now = new Date()) => {
    const statuses = startHourStatus(range.start, now);
    if (statuses[hour] !== "ok") return range;
    const start = withHour(range.start, hour);
    return { start, end: fitEnd(start, range.end) };
  };

  const setEndHour = (hour, range) => {
    const statuses = endHourStatus(range.end, range.start);
    if (statuses[hour] !== "ok") return range;
    return { start: range.start, end: withHour(range.end, hour) };
  };

  const defaultRange = (now = new Date()) => fitRange(dates.defaultRange(now), now);

  return Object.freeze({
    busy,
    hasBusy: busy.length > 0,
    busyAt,
    endLimit,
    isFreeStart,
    isRangeFree,
    startHourStatus,
    endHourStatus,
    windowsOnDay,
    dayBooking,
    pickStart,
    pickEnd,
    fitEnd,
    withLength,
    maxTripHoursFrom,
    fitRange,
    setStartHour,
    setEndHour,
    defaultRange,
  });
};
